/** PRO-07 Commandes · PRO-08 Détail · PRO-09 Page de paiement PSP (SIMULÉE) · PRO-10 Factures · PRO-11 Document spécimen · PRO-01 Tableau de bord. */
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Choice, Logo, Modal, Pagination, SearchField, Steps, Table, Tabs, useToast, type Etape } from '@myron/design-system';
import { CreditCard, FileText, Printer, RotateCcw, XCircle } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { get, messageErreur, nouvelleCleIdempotence, post } from '../../shared/api/client';
import { SiPermission, useSession } from '../../shared/auth/session';
import { Confirmation, DecisionOuverte, EntetePage, ErreurFormulaire, EtatRequete, MentionSimule, StatutBadge } from '../../shared/ui/composants';
import { dateCourte, dateHeure, euros, LIBELLE_MODE_REMISE, LIBELLE_MOYEN } from '../../shared/ui/format';
import { cles, LIBELLE_SOURCE, useCommande, useEntrepots, type Commande, type CommandeResume, type Facture, type ProduitPro } from './api';

const LIB_COND: Record<string, string> = { PALETTE: 'Palette', CARTON: 'Carton', PACK: 'Pack' };
const FILTRES: Record<string, string[] | undefined> = {
  toutes: undefined,
  attente: ['EN_ATTENTE_PAIEMENT', 'PAYEE_STOCK_INSUFFISANT'],
  encours: ['PAYEE', 'EN_PREPARATION', 'EN_PREPARATION_NON_RESERVEE', 'PRETE', 'EXPEDIEE'],
  terminees: ['LIVREE', 'RETIREE', 'ANNULEE', 'EXPIREE'],
};

function useOuvrirDocument() {
  const nav = useNavigate();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, facture }: { id: string; facture?: boolean }) => post<S<'LienTelechargement'>>(facture ? `/pro/factures/${id}/telechargement` : `/pro/documents/${id}/telechargement`),
    onSuccess: (l) => nav(l.url!),
    onError: (e) => toast({ ton: 'erreur', titre: 'Document indisponible', texte: messageErreur(e) }),
  });
}

export function TableauDeBordPro() {
  const { session } = useSession();
  const q = useQuery({ queryKey: ['pro', 'tableau'], queryFn: () => get<S<'TableauDeBordPro'>>('/pro/tableau-de-bord') });
  return (
    <>
      <EntetePage titre={`Bonjour ${session?.utilisateur.prenom ?? ''}`} sousTitre={session?.entreprise?.raisonSociale} actions={<SiPermission permission="prix.voir"><Link className="my-btn my-btn--primaire" to="/pro/catalogue">Commander</Link></SiPermission>} />
      <EtatRequete
        requete={q}
        enfants={(d) => (
          <div className="my-pile my-pile--large">
            {(d.alertes ?? []).map((a) => <Alert key={a} ton="alerte">{a}</Alert>)}
            <div className="my-grille">
              <Card className="my-kpi"><span className="my-secondaire">Commandes en cours</span><span className="my-kpi__valeur">{d.commandesEnCours?.length ?? 0}</span></Card>
              <Card className="my-kpi"><span className="my-secondaire">En attente de paiement</span><span className="my-kpi__valeur">{d.commandesEnAttentePaiement ?? 0}</span></Card>
              <Card className="my-kpi"><span className="my-secondaire">Dernières factures</span><span className="my-kpi__valeur">{d.dernieresFactures?.length ?? 0}</span></Card>
            </div>
            <section className="my-pile" aria-labelledby="t-encours">
              <div className="my-ligne my-ligne--espace"><h2 id="t-encours" className="my-titre-3">Commandes en cours</h2><Link to="/pro/commandes">Toutes les commandes</Link></div>
              {d.commandesEnCours?.length ? <TableCommandes lignes={d.commandesEnCours} /> : <p className="my-secondaire">Aucune commande en cours.</p>}
            </section>
            {!!d.produitsFrequents?.length && (
              <section className="my-pile" aria-labelledby="t-freq">
                <h2 id="t-freq" className="my-titre-3">Vos produits fréquents</h2>
                <div className="my-grille">
                  {(d.produitsFrequents as ProduitPro[]).map((p) => {
                    const o = p.offres?.find((x) => x.conditionnement?.parDefaut) ?? p.offres?.[0];
                    return (
                      <Card key={p.id} as="article">
                        <div className="my-pile my-pile--serre">
                          <Link to={`/pro/catalogue/${p.id}`}><strong>{p.libelle}</strong></Link>
                          <span className="my-legende my-secondaire">Réf. {p.sku}</span>
                          {o && <span className="my-prix">{euros(o.prixUnitaireHtCentimes)} <span className="my-legende my-secondaire">HT / {LIB_COND[o.conditionnement!.type!]?.toLowerCase()}</span></span>}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      />
    </>
  );
}

function TableCommandes({ lignes }: { lignes: CommandeResume[] }) {
  return (
    <Table<CommandeResume>
      legende="Commandes"
      lignes={lignes}
      cle={(c) => c.id!}
      colonnes={[
        { cle: 'ref', titre: 'Référence', rendu: (c) => <Link to={`/pro/commandes/${c.id}`}><strong>{c.reference}</strong></Link> },
        { cle: 'date', titre: 'Date', rendu: (c) => dateCourte(c.creeLe) },
        { cle: 'statut', titre: 'Statut', rendu: (c) => <StatutBadge statut={c.statut!} /> },
        { cle: 'remise', titre: 'Remise', rendu: (c) => LIBELLE_MODE_REMISE[c.modeRemise!] },
        { cle: 'paiement', titre: 'Paiement', rendu: (c) => LIBELLE_MOYEN[c.moyenPaiement!] },
        { cle: 'total', titre: 'Total TTC', num: true, rendu: (c) => euros(c.totalTtcCentimes) },
      ]}
    />
  );
}

export function ListeCommandes() {
  const [params, setParams] = useSearchParams();
  const filtre = (params.get('filtre') ?? 'toutes') as keyof typeof FILTRES;
  const [saisie, setSaisie] = useState(params.get('q') ?? '');
  const requete = { statut: FILTRES[filtre]?.join(','), q: params.get('q') ?? undefined, page: Number(params.get('page') ?? 1), taille: 20 };
  const q = useQuery({ queryKey: cles.commandes(requete), queryFn: () => get<Page<CommandeResume>>('/pro/commandes', requete), placeholderData: (x) => x });
  const changer = (cle: string, v?: string) => { const n = new URLSearchParams(params); if (v) n.set(cle, v); else n.delete(cle); if (cle !== 'page') n.delete('page'); setParams(n); };
  return (
    <>
      <EntetePage titre="Commandes" sousTitre="Commandes de votre entreprise." />
      <Tabs label="Filtrer les commandes" actif={filtre} onChange={(id) => changer('filtre', id === 'toutes' ? undefined : id)} onglets={[{ id: 'toutes', libelle: 'Toutes' }, { id: 'attente', libelle: 'À payer / à décider' }, { id: 'encours', libelle: 'En cours' }, { id: 'terminees', libelle: 'Terminées' }]} />
      <form role="search" className="my-ligne" style={{ marginBottom: 16 }} onSubmit={(e) => { e.preventDefault(); changer('q', saisie.trim() || undefined); }}>
        <div style={{ flex: 1, minWidth: 220 }}><SearchField label="Rechercher une commande" placeholder="Référence ou produit" value={saisie} onChange={(e) => setSaisie(e.target.value)} /></div>
        <Button type="submit" variante="secondaire">Rechercher</Button>
      </form>
      <EtatRequete
        requete={q}
        vide={(d) => d.donnees.length === 0}
        titreVide="Aucune commande"
        actionVide={<Link className="my-btn my-btn--primaire" to="/pro/catalogue">Parcourir le catalogue</Link>}
        enfants={(d) => <div className="my-pile"><TableCommandes lignes={d.donnees} /><Pagination page={d.page} taille={d.taille} total={d.total} onPage={(p) => changer('page', String(p))} /></div>}
      />
    </>
  );
}

function etapesCommande(c: Commande): Etape[] {
  const ordre = c.modeRemise === 'RETRAIT' ? ['EN_ATTENTE_PAIEMENT', 'PAYEE', 'EN_PREPARATION', 'PRETE', 'RETIREE'] : ['EN_ATTENTE_PAIEMENT', 'PAYEE', 'EN_PREPARATION', 'PRETE', 'EXPEDIEE', 'LIVREE'];
  const titres: Record<string, string> = { EN_ATTENTE_PAIEMENT: 'Commandée', PAYEE: 'Payée', EN_PREPARATION: 'Préparation', PRETE: 'Prête', RETIREE: 'Retirée', EXPEDIEE: 'Expédiée', LIVREE: 'Livrée' };
  const statut = c.statut === 'EN_PREPARATION_NON_RESERVEE' ? 'EN_PREPARATION' : c.statut === 'PAYEE_STOCK_INSUFFISANT' ? 'PAYEE' : c.statut!;
  const i = ordre.indexOf(statut);
  return ordre.map((s, k) => ({ titre: titres[s]!, etat: i < 0 ? 'avenir' : k < i ? 'fait' : k === i ? (k === ordre.length - 1 ? 'fait' : 'encours') : 'avenir' }));
}

export function DetailCommande() {
  const { commandeId = '' } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useCommande(commandeId);
  const entrepots = useEntrepots();
  const docs = useOuvrirDocument();
  const [annulation, setAnnulation] = useState(false);
  const [choix, setChoix] = useState<string | null>(null);
  const [modaleChoix, setModaleChoix] = useState(false);
  const clePaiement = useRef<string | null>(null);
  const rafraichir = (c: Commande) => { qc.setQueryData(cles.commande(commandeId), c); void qc.invalidateQueries({ queryKey: ['pro', 'commandes'] }); };

  const annuler = useMutation({
    mutationFn: ({ motif, version }: { motif: string; version: number }) => post<Commande>(`/pro/commandes/${commandeId}/annuler`, { motif: motif || 'Annulation par le client' }, { version }),
    onSuccess: (c) => { rafraichir(c); setAnnulation(false); toast({ ton: 'succes', titre: 'Commande annulée' }); },
  });
  const payer = useMutation({
    mutationFn: () => { clePaiement.current ??= nouvelleCleIdempotence(); return post<S<'InitiationPaiement'>>(`/pro/commandes/${commandeId}/paiement`, undefined, { idempotence: clePaiement.current }); },
    onSuccess: async (p) => { clePaiement.current = null; await qc.invalidateQueries({ queryKey: cles.commande(commandeId) }); if (p.urlRedirection) nav(p.urlRedirection); },
  });
  const decider = useMutation({
    mutationFn: ({ option, version }: { option: string; version: number }) => post<Commande>(`/pro/commandes/${commandeId}/stock-insuffisant/choix`, { option }, { version }),
    onSuccess: (c) => { rafraichir(c); setModaleChoix(false); toast({ ton: 'succes', titre: 'Décision enregistrée' }); },
  });
  const recommander = useMutation({
    mutationFn: () => post(`/pro/commandes/${commandeId}/recommander`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: cles.panier }); nav('/pro/panier'); },
  });

  return (
    <EtatRequete
      requete={q}
      enfants={(c) => {
        const actions = c.actionsPossibles ?? [];
        const paiementEnAttente = c.paiements?.some((p) => p.statut === 'EN_ATTENTE' && p.moyen === 'CB_EN_LIGNE');
        const echec = c.paiements?.some((p) => p.statut === 'ECHOUE');
        return (
          <>
            <EntetePage
              titre={`Commande ${c.reference}`}
              sousTitre={<>Passée le {dateHeure(c.creeLe)} par {c.passeePar} · <StatutBadge statut={c.statut!} /></>}
              fil={[{ libelle: 'Commandes', vers: '/pro/commandes' }, { libelle: c.reference! }]}
              actions={
                <>
                  {actions.includes('PAYER') && <Button icone={CreditCard} chargement={payer.isPending} onClick={() => payer.mutate()}>{c.moyenPaiement === 'CB_RETRAIT' ? 'Payer l’acompte en ligne' : 'Payer par carte'}</Button>}
                  {actions.includes('RECOMMANDER') && <Button variante="secondaire" icone={RotateCcw} chargement={recommander.isPending} onClick={() => recommander.mutate()}>Commander à nouveau</Button>}
                  {actions.includes('ANNULER') && <Button variante="danger" icone={XCircle} onClick={() => setAnnulation(true)}>Annuler la commande</Button>}
                </>
              }
            />
            <div className="my-pile">
              {params.get('nouvelle') && <Alert ton="succes" titre="Commande enregistrée">Un e-mail de confirmation vous a été envoyé (boîte simulée).</Alert>}
              {params.get('retourPsp') && c.statut === 'EN_ATTENTE_PAIEMENT' && !echec && <Alert ton="info" titre="Paiement en cours de confirmation">Votre commande sera confirmée à réception de la notification du prestataire de paiement. Le retour sur cette page ne vaut pas paiement.</Alert>}
              {echec && c.statut === 'EN_ATTENTE_PAIEMENT' && <Alert ton="erreur" titre="Paiement refusé">Le paiement par carte a été refusé. Vous pouvez réessayer.</Alert>}
              <ErreurFormulaire erreur={payer.error ?? recommander.error} />
              {c.statut === 'PAYEE_STOCK_INSUFFISANT' && (
                <DecisionOuverte id="DO-04">
                  Votre paiement est confirmé mais le stock est insuffisant.{' '}
                  {actions.includes('CHOISIR_TRAITEMENT_STOCK') ? <Button compact onClick={() => setModaleChoix(true)}>Choisir la suite</Button> : c.choixStockInsuffisant ? `Votre choix : ${c.choixStockInsuffisant}.` : 'Notre équipe vous contacte.'}
                </DecisionOuverte>
              )}
              {!['ANNULEE', 'EXPIREE'].includes(c.statut!) && <Steps etapes={etapesCommande(c)} label="Suivi de la commande" />}
              <div className="my-grille-2">
                <Card as="section" aria-labelledby="t-lignes">
                  <div className="my-pile">
                    <h2 id="t-lignes" className="my-titre-4">Produits (prix figés à la commande)</h2>
                    <Table
                      legende="Lignes de la commande"
                      lignes={c.lignes ?? []}
                      cle={(l) => l.id!}
                      colonnes={[
                        { cle: 'p', titre: 'Produit', rendu: (l) => <span className="my-pile my-pile--serre" style={{ gap: 2 }}><strong>{l.libelleFige}</strong><span className="my-legende my-secondaire">Réf. {l.sku}</span></span> },
                        { cle: 'q', titre: 'Quantité', rendu: (l) => `${l.quantite} × ${LIB_COND[l.conditionnementType!]}` },
                        { cle: 'pu', titre: 'Prix unitaire HT', num: true, rendu: (l) => <span title={LIBELLE_SOURCE[l.sourcePrix!]}>{euros(l.prixUnitaireHtCentimes)}</span> },
                        { cle: 't', titre: 'Total HT', num: true, rendu: (l) => euros(l.totalHtCentimes) },
                      ]}
                    />
                    <dl className="my-dl">
                      <dt>Total HT</dt><dd>{euros(c.totalHtCentimes)}</dd>
                      <dt>Frais de livraison</dt><dd>{c.fraisLivraisonCentimes ? euros(c.fraisLivraisonCentimes) : 'Offerts'}</dd>
                      <dt>TVA</dt><dd>{euros(c.totalTvaCentimes)}</dd>
                      <dt>Total TTC</dt><dd className="my-prix">{euros(c.totalTtcCentimes)}</dd>
                    </dl>
                  </div>
                </Card>
                <div className="my-pile">
                  <Card as="section" aria-labelledby="t-paiement">
                    <div className="my-pile my-pile--serre">
                      <h2 id="t-paiement" className="my-titre-4">Paiement — {LIBELLE_MOYEN[c.moyenPaiement!]}</h2>
                      {c.moyenPaiement === 'VIREMENT' && c.statut === 'EN_ATTENTE_PAIEMENT' && (
                        <>
                          <p>Effectuez votre virement avec la référence :</p>
                          <p><code className="my-code" data-testid="reference-virement">{c.referenceVirementAttendue}</code></p>
                          <p className="my-petit">IBAN : <code className="my-code">{c.ibanReception}</code></p>
                          <Alert ton="alerte">Le stock n’est réservé qu’à réception des fonds. Sans virement reçu avant le {dateHeure(c.expireLe)}, la commande expire.</Alert>
                        </>
                      )}
                      {c.moyenPaiement === 'CB_RETRAIT' && c.statut === 'EN_ATTENTE_PAIEMENT' && <p>Paiement par carte au guichet lors du retrait. {c.varianteSimulee && <Badge ton="info">Variante {c.varianteSimulee} (DO-01)</Badge>}</p>}
                      {c.moyenPaiement === 'CB_EN_LIGNE' && c.statut === 'EN_ATTENTE_PAIEMENT' && <p>{paiementEnAttente ? 'Paiement par carte en attente de confirmation.' : 'Paiement par carte à effectuer.'}</p>}
                      <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
                        {(c.paiements ?? []).map((p) => (
                          <li key={p.id} className="my-ligne my-ligne--espace"><span>{LIBELLE_MOYEN[p.moyen!]}{p.acompte ? ' (acompte)' : ''} · {euros(p.montantCentimes)}</span><StatutBadge statut={p.statut!} /></li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                  <Card as="section" aria-labelledby="t-remise">
                    <div className="my-pile my-pile--serre">
                      <h2 id="t-remise" className="my-titre-4">{LIBELLE_MODE_REMISE[c.modeRemise!]}</h2>
                      <p>{entrepots.data?.find((e) => e.id === c.entrepotId)?.nom ?? 'Entrepôt'}</p>
                      {c.adresseLivraison && <p className="my-petit">{c.adresseLivraison.ligne1}, {c.adresseLivraison.codePostal} {c.adresseLivraison.ville}</p>}
                      {c.creneauRetrait && <p className="my-petit">Le {dateCourte(c.creneauRetrait.date)} entre {c.creneauRetrait.debut} et {c.creneauRetrait.fin}</p>}
                      {c.codeRetrait && <p>Code de retrait : <code className="my-code" data-testid="code-retrait">{c.codeRetrait}</code></p>}
                    </div>
                  </Card>
                  {!!c.documents?.length && (
                    <Card as="section" aria-labelledby="t-docs">
                      <div className="my-pile my-pile--serre">
                        <h2 id="t-docs" className="my-titre-4">Documents</h2>
                        {c.documents.map((d) => (
                          <Button key={d.id} variante="secondaire" compact icone={FileText} chargement={docs.isPending && docs.variables?.id === d.id} onClick={() => docs.mutate({ id: d.id })}>{d.type === 'FACTURE' ? 'Facture' : d.type === 'BON_LIVRAISON' ? 'Bon de livraison' : d.type === 'BON_RETRAIT' ? 'Bon de retrait' : d.type} {d.numero}</Button>
                        ))}
                      </div>
                    </Card>
                  )}
                  <Card as="section" aria-labelledby="t-hist">
                    <div className="my-pile my-pile--serre">
                      <h2 id="t-hist" className="my-titre-4">Historique</h2>
                      <ol className="my-petit" style={{ margin: 0, paddingLeft: 18 }}>
                        {(c.historique ?? []).map((h, i) => <li key={i}>{dateHeure(h.horodatage)} — <StatutBadge statut={h.statutApres!} /> <span className="my-secondaire">({h.auteur})</span></li>)}
                      </ol>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
            <Confirmation
              ouverte={annulation}
              titre="Annuler la commande ?"
              texte="La commande sera annulée. Aucun stock n’était réservé (paiement non confirmé)."
              libelleAction="Annuler la commande"
              danger
              motif="optionnel"
              chargement={annuler.isPending}
              erreur={annuler.error}
              onFermer={() => setAnnulation(false)}
              onConfirmer={(motif) => annuler.mutate({ motif, version: c.version! })}
            />
            <Modal ouverte={modaleChoix} titre="Stock insuffisant : que souhaitez-vous ?" onFermer={() => setModaleChoix(false)} actions={<><Button variante="tertiaire" onClick={() => setModaleChoix(false)}>Plus tard</Button><Button disabled={!choix} chargement={decider.isPending} onClick={() => decider.mutate({ option: choix!, version: c.version! })}>Valider mon choix</Button></>}>
              <fieldset className="my-pile my-pile--serre" style={{ border: 0, padding: 0 }}>
                <legend className="my-petit my-secondaire">Décision ouverte DO-04 — options simulées.</legend>
                <Choice type="radio" name="choix-stock" label="Livraison partielle et remboursement du manquant" checked={choix === 'PARTIEL_ET_REMBOURSEMENT'} onChange={() => setChoix('PARTIEL_ET_REMBOURSEMENT')} />
                <Choice type="radio" name="choix-stock" label="Attendre le réapprovisionnement" checked={choix === 'ATTENTE_REAPPRO'} onChange={() => setChoix('ATTENTE_REAPPRO')} />
                <Choice type="radio" name="choix-stock" label="Annuler et être remboursé intégralement" checked={choix === 'ANNULATION_TOTALE'} onChange={() => setChoix('ANNULATION_TOTALE')} />
              </fieldset>
              <ErreurFormulaire erreur={decider.error} />
            </Modal>
          </>
        );
      }}
    />
  );
}

/** Page de paiement du prestataire — SIMULÉE. En production, page hébergée par le PSP (aucune donnée carte chez MYRON). */
export function PaiementPspSimule() {
  const { paiementId = '' } = useParams();
  const [params] = useSearchParams();
  const commandeId = params.get('commande') ?? '';
  const nav = useNavigate();
  const qc = useQueryClient();
  // Toujours relire : la session de paiement vient d'être créée.
  const q = useQuery({ queryKey: cles.commande(commandeId), queryFn: () => get<Commande>(`/pro/commandes/${commandeId}`), staleTime: 0, refetchOnMount: 'always' });
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);
  const p = q.data?.paiements?.find((x) => x.id === paiementId);
  const emettre = async (type: 'PAIEMENT_ACCEPTE' | 'PAIEMENT_REFUSE') => {
    setEnvoi(type);
    setErreur(null);
    try {
      // Le PSP notifie le serveur MYRON par webhook signé : c'est la seule source de confirmation.
      await post('/_simulation/evenement-psp', { paiementId, type });
      await qc.invalidateQueries({ queryKey: cles.commande(commandeId) });
      nav(`/pro/commandes/${commandeId}?retourPsp=1`);
    } catch (e) { setErreur(e); } finally { setEnvoi(null); }
  };
  return (
    <div className="my-pile" style={{ maxWidth: 560, margin: '0 auto' }}>
      <Alert ton="info" titre="Prestataire de paiement SIMULÉ">Cette page imite la page hébergée du prestataire (DO-13). Aucune carte n’est saisie, aucune transaction réelle. <MentionSimule /></Alert>
      <Card as="section" aria-labelledby="t-psp">
        <EtatRequete
          requete={q}
          enfants={(c) => (
            <div className="my-pile">
              <h1 id="t-psp" className="my-titre-3">Paiement sécurisé</h1>
              <dl className="my-dl">
                <dt>Marchand</dt><dd>MYRON (simulation)</dd>
                <dt>Commande</dt><dd>{c.reference}</dd>
                <dt>Montant</dt><dd className="my-prix" data-testid="montant-psp">{euros(p?.montantCentimes ?? c.totalTtcCentimes)}</dd>
              </dl>
              {!p ? <Alert ton="erreur">Session de paiement introuvable.</Alert> : p.statut !== 'EN_ATTENTE' ? <Alert ton="alerte">Ce paiement n’est plus en attente ({p.statut}).</Alert> : (
                <div className="my-ligne">
                  <Button chargement={envoi === 'PAIEMENT_ACCEPTE'} onClick={() => emettre('PAIEMENT_ACCEPTE')}>Payer (paiement accepté)</Button>
                  <Button variante="danger" chargement={envoi === 'PAIEMENT_REFUSE'} onClick={() => emettre('PAIEMENT_REFUSE')}>Refuser le paiement</Button>
                </div>
              )}
              <Button variante="tertiaire" onClick={() => nav(`/pro/commandes/${commandeId}?retourPsp=1`)}>Revenir au site sans payer</Button>
              <ErreurFormulaire erreur={erreur} />
            </div>
          )}
        />
      </Card>
    </div>
  );
}

export function ListeFactures() {
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['pro', 'factures', page], queryFn: () => get<Page<Facture>>('/pro/factures', { page, taille: 20 }), placeholderData: (x) => x });
  const docs = useOuvrirDocument();
  return (
    <>
      <EntetePage titre="Factures" sousTitre="Documents fictifs marqués SPÉCIMEN (facturation réelle : décision ouverte DO-21)." />
      <EtatRequete
        requete={q}
        vide={(d) => d.donnees.length === 0}
        titreVide="Aucune facture"
        enfants={(d) => (
          <div className="my-pile">
            <Table<Facture>
              legende="Factures"
              lignes={d.donnees}
              cle={(f) => f.id!}
              colonnes={[
                { cle: 'n', titre: 'Numéro', rendu: (f) => <span className="my-ligne"><strong>{f.numero}</strong>{f.specimen && <Badge ton="sable">SPÉCIMEN</Badge>}</span> },
                { cle: 'c', titre: 'Commande', rendu: (f) => <Link to={`/pro/commandes/${f.commandeId}`}>{f.commandeReference}</Link> },
                { cle: 'd', titre: 'Date', rendu: (f) => dateCourte(f.emiseLe) },
                { cle: 'm', titre: 'Montant TTC', num: true, rendu: (f) => euros(f.totalTtcCentimes) },
                { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (f) => <Button compact variante="secondaire" icone={FileText} chargement={docs.isPending && docs.variables?.id === f.id} onClick={() => docs.mutate({ id: f.id!, facture: true })}>Ouvrir</Button> },
              ]}
            />
            <Pagination page={d.page} taille={d.taille} total={d.total} onPage={setPage} />
          </div>
        )}
      />
    </>
  );
}

/** Document SPÉCIMEN imprimable (lien à usage court obtenu via l'API). */
export function DocumentSpecimen() {
  const { documentId = '' } = useParams();
  const [params] = useSearchParams();
  const { session } = useSession();
  const q = useCommande(params.get('commande') ?? '');
  return (
    <EtatRequete
      requete={q}
      enfants={(c) => {
        const d = c.documents?.find((x) => x.id === documentId);
        if (!d) return <Alert ton="alerte" titre="Document introuvable">Ce document n’existe pas ou le lien a expiré.</Alert>;
        const titre = d.type === 'FACTURE' ? 'Facture' : d.type === 'BON_LIVRAISON' ? 'Bon de livraison' : d.type === 'BON_RETRAIT' ? 'Bon de retrait' : 'Document';
        return (
          <div className="my-pile">
            <div className="my-ligne my-ligne--espace">
              <Link to={`/pro/commandes/${c.id}`}>← Retour à la commande</Link>
              <Button variante="secondaire" icone={Printer} onClick={() => window.print()}>Imprimer</Button>
            </div>
            <Card as="article" aria-labelledby="t-doc">
              <div className="my-pile">
                <div className="my-ligne my-ligne--espace"><Logo /><Badge ton="sable">SPÉCIMEN — sans valeur comptable</Badge></div>
                <h1 id="t-doc" className="my-titre-2">{titre} {d.numero}</h1>
                <dl className="my-dl">
                  <dt>Client</dt><dd>{session?.entreprise?.raisonSociale}</dd>
                  <dt>Commande</dt><dd>{c.reference} du {dateCourte(c.creeLe)}</dd>
                  <dt>Émis le</dt><dd>{dateCourte(d.creeLe)}</dd>
                </dl>
                <Table legende="Lignes" lignes={c.lignes ?? []} cle={(l) => l.id!} colonnes={[
                  { cle: 'p', titre: 'Désignation', rendu: (l) => `${l.libelleFige} (${l.sku})` },
                  { cle: 'q', titre: 'Qté', rendu: (l) => `${l.quantite} × ${LIB_COND[l.conditionnementType!]}` },
                  { cle: 'pu', titre: 'PU HT', num: true, rendu: (l) => euros(l.prixUnitaireHtCentimes) },
                  { cle: 'tva', titre: 'TVA', num: true, rendu: (l) => `${(l.tvaBp ?? 0) / 100} %` },
                  { cle: 't', titre: 'Total HT', num: true, rendu: (l) => euros(l.totalHtCentimes) },
                ]} />
                <dl className="my-dl" style={{ justifySelf: 'end' }}>
                  <dt>Total HT</dt><dd>{euros(c.totalHtCentimes)}</dd>
                  <dt>Frais de livraison HT</dt><dd>{euros(c.fraisLivraisonCentimes)}</dd>
                  <dt>TVA</dt><dd>{euros(c.totalTvaCentimes)}</dd>
                  <dt>Total TTC</dt><dd><strong>{euros(c.totalTtcCentimes)}</strong></dd>
                </dl>
                <p className="my-legende my-secondaire">Document généré par la maquette MYRON à partir de données fictives.</p>
              </div>
            </Card>
          </div>
        );
      }}
    />
  );
}
